import moment from "moment";
import firebase from "firebase/compat";
import type { Listing } from "../../@types/listings/listing";
import type {
  EditableListing,
  AddressCoordinates,
  ListingWithAdditionalData
} from "../../@types/listings/new";
import type {
  ListingContact,
  AdditionalUser,
  AdditionalUserWithFee
} from "../../@types/listings/user";
import type { FilePreview } from "../../@types/listings/fileData";
import type { ImagePreview } from "../../@types/listings/imageData";
import type { ProcessedListingAddress } from "../../@types/sharedSchema";

export const initialFormColumns: EditableListing = {
  fee: null,
  price: null,
  rooms: null,
  floor: null,
  files: null,
  status: "onSale",
  hasBars: false,
  pictures: null,
  signDate: null,
  hasGarden: false,
  visibility: "private",
  hasParking: false,
  hasTerrace: false,
  hasStorage: false,
  floorOutOf: null,
  purposeType: null,
  projectName: "",
  listingType: null,
  housingUnit: false,
  hasElevator: false,
  exclusivity: null,
  leaseStartDate: null,
  leaseEndDate: null,
  videoUrl: null,
  description: "",
  cleaningFee: null,
  streetNumber: null,
  listingState: null,
  evictionDate: null,
  evictionType: "specificDate",
  sizeOfPlotM2: null,
  protectedSpace: false,
  hasMamak: false,
  hasMiklat: false,
  sizeOfGardenM2: null,
  locationDetails: "",
  additionalNotes: "",
  apartmentNumber: null,
  sizeOfTerraceM2: null,
  transactionType: null,
  feeIncludesTaxes: false,
  sizeOfPropertyM2: null,
  parkingSpotsCount: null,
  finalListingPrice: null,
  buyerAttorneyName: "",
  municipalityTaxes: null,
  addressDisplayMode: null,
  hasAirConditioning: false,
  sellerAttorneyName: "",
  addressAreaNickname: "",
  dealAdditionDetails: "",
  userDefinedListingId: "",
  accessibleForDisabled: false,
  buyerAttorneyPhoneNumber: "",
  sellerAttorneyPhoneNumber: "",
  madlanBulletinId: null,
  madlanConfirmedAddress: null,
  madlanLocationResolution: null,
  madlanPublishedByWebotUserMail: null
};

export function setInitialListing(listing: undefined | ListingWithAdditionalData) {
  return (): EditableListing => {
    if (!listing) return initialFormColumns;

    const {
      id,
      price,
      favorite,
      signDate,
      createdTime,
      exclusivity,
      evictionDate,
      evictionType,
      leaseEndDate,
      leaseStartDate,
      finalListingPrice,
      ...newEditableFormColumns
    } = listing;

    const parsedEvictionType = evictionType ?? "specificDate";
    const formattedSignDate = convertTimestampToDateString(signDate);
    const formattedEvictionDate = convertTimestampToDateString(evictionDate);
    const formattedExclusivity = convertTimestampToDateString(exclusivity);
    // Absent on every listing saved before the lease fields existed: the conversion turns
    // `undefined` into `null`, which is what keeps the unsaved-changes baseline (built from
    // this same function) from reading "changed" on a listing nobody touched.
    const formattedLeaseStartDate = convertTimestampToDateString(leaseStartDate);
    const formattedLeaseEndDate = convertTimestampToDateString(leaseEndDate);

    const newFinalListingPrice = finalListingPrice ?? price;

    return {
      ...newEditableFormColumns,
      // ממ"ק / מקלט do not exist on a listing saved before 2026-10. The form needs real
      // booleans, and the dialog's unsaved-changes check builds its baseline from this
      // same function: left `undefined`, ticking and unticking one of them would compare
      // `undefined` to `false` and ask "discard changes?" on a listing nobody changed.
      hasMamak: listing.hasMamak ?? false,
      hasMiklat: listing.hasMiklat ?? false,
      price: price,
      signDate: formattedSignDate,
      exclusivity: formattedExclusivity,
      leaseStartDate: formattedLeaseStartDate,
      leaseEndDate: formattedLeaseEndDate,
      evictionType: parsedEvictionType,
      evictionDate: formattedEvictionDate,
      finalListingPrice: newFinalListingPrice
    };
  };
}

export function setInitialImages(editableListing: EditableListing) {
  return (): ImagePreview[] => {
    const { pictures } = editableListing ?? {};

    if (!pictures) return [];

    return pictures;
  };
}

export function setInitialFiles(editableListing: EditableListing) {
  return (): FilePreview[] => {
    const { files } = editableListing ?? {};

    if (!files) return [];

    return files;
  };
}

export function setInitialOwnersData(listing: Pick<Listing, "ownersData">) {
  return (): AdditionalUserWithFee[] => {
    const { ownersData } = listing ?? {};

    return ownersData ?? [];
  };
}

export function setInitialListingContacts(listing: Pick<Listing, "listingContacts">) {
  return (): ListingContact[] => {
    const { listingContacts } = listing ?? {};

    return listingContacts ?? [];
  };
}

export function setInitialBuyerOriginData(listing: Pick<Listing, "buyerOriginData">) {
  return (): AdditionalUserWithFee[] => {
    const { buyerOriginData } = listing ?? {};

    return buyerOriginData ?? [];
  };
}

export function setInitialAddress(listing: Pick<Listing, "address" | "city" | "neighborhood">) {
  return (): ProcessedListingAddress[] => {
    const { address, city, neighborhood } = listing ?? {};

    const emptyAddress = !address && !city;

    if (emptyAddress) return [];

    return [
      {
        city: city ?? "",
        street: address ?? "",
        neighborhood: neighborhood ?? ""
      }
    ];
  };
}

export function setInitialAddressCoordinates(listing: Pick<Listing, "addressCoordinates">) {
  return (): AddressCoordinates | null => {
    const { addressCoordinates } = listing ?? {};

    if (!addressCoordinates) return null;

    return addressCoordinates;
  };
}

export function convertDateStringToTimestamp(
  dateString: null | firebase.firestore.Timestamp
): firebase.firestore.Timestamp | null {
  if (!dateString) return null;

  const date = new Date(dateString as unknown as string);

  return firebase.firestore.Timestamp.fromDate(date);
}

export function convertTimestampToDateString(
  timestamp: null | firebase.firestore.Timestamp
): firebase.firestore.Timestamp | null {
  if (!timestamp) return null;

  return moment(timestamp.toDate()).format("YYYY-MM-DD") as unknown as firebase.firestore.Timestamp;
}

export function calculateFee(
  finalFee: null | number,
  price: number,
  includesTaxes: boolean
): null | number {
  if (!finalFee) return null;

  const calculatedFee = includesTaxes ? finalFee : (finalFee * 100) / 118;
  const totalFee = (calculatedFee / price) * 100;

  return totalFee;
}

export function calculateFinalFee(
  fee: null | number,
  price: number,
  includesTaxes: boolean
): null | number {
  if (!fee) return null;

  const calculatedFinalFee = price * (fee / 100);
  const totalFinalFee = includesTaxes ? calculatedFinalFee : calculatedFinalFee * 1.18;

  return totalFinalFee;
}

export function calculateTotalFinalFee(
  personsFeeData: AdditionalUserWithFee[],
  options?: {
    type: "organization" | "external";
    organizationUsers: AdditionalUser[];
  }
): null | number {
  if (!personsFeeData?.length) return null;

  const totalFinalFee = personsFeeData.reduce((acc, { email, finalFee }) => {
    if (!finalFee) return acc;

    if (options) {
      const { type, organizationUsers } = options;

      const personIsInOrganization = organizationUsers.find((user) => user.email === email);

      const onlyOrganizationUsers = type === "organization" && personIsInOrganization;
      const onlyExternalUsers = type === "external" && !personIsInOrganization;

      if (onlyOrganizationUsers || onlyExternalUsers) return acc + finalFee;

      return acc;
    }

    return acc + finalFee;
  }, 0);

  return totalFinalFee || null;
}
